<script lang="ts" setup>
import 'vue-sonner/style.css'
import { CircleCheck, Info, Loader, OctagonX, TriangleAlert, X } from 'lucide'
import { Toaster as Sonner } from 'vue-sonner'
import { SphIcon } from '@/components/base/icon'
import type { ToasterProps } from 'vue-sonner'
import { cn } from '@/utils/shadcn/lib/utils'

defineOptions({
    compatConfig: { MODE: 3 },
})

const props = withDefaults(defineProps<ToasterProps>(), {
    class: '',
    richColors: true,
    closeButtonPosition: 'top-right',
    position: 'top-center',
})
</script>

<template>
    <Sonner
        :class="cn('toaster group', props.class)"
        :style="{
            '--border-radius': 'var(--radius)',

            '--normal-bg': 'var(--color-popover)',
            '--normal-text': 'var(--color-popover-foreground)',
            '--normal-border': 'var(--color-popover-border)',

            '--info-bg': 'var(--color-info)',
            '--info-text': 'var(--color-info-foreground)',
            '--info-border': 'var(--color-info-border)',

            '--success-bg': 'var(--color-success)',
            '--success-text': 'var(--color-success-foreground)',
            '--success-border': 'var(--color-success-border)',

            '--warning-bg': 'var(--color-warning)',
            '--warning-text': 'var(--color-warning-foreground)',
            '--warning-border': 'var(--color-warning-border)',

            '--error-bg': 'var(--color-error)',
            '--error-text': 'var(--color-error-foreground)',
            '--error-border': 'var(--color-error-border)',
        }"
        v-bind="props"
    >
        <template #success-icon>
            <SphIcon
                :icon="CircleCheck"
                size="xl"
            />
        </template>
        <template #info-icon>
            <SphIcon
                :icon="Info"
                size="xl"
            />
        </template>
        <template #warning-icon>
            <SphIcon
                :icon="TriangleAlert"
                size="xl"
            />
        </template>
        <template #error-icon>
            <SphIcon
                :icon="OctagonX"
                size="xl"
            />
        </template>
        <template #loading-icon>
            <SphIcon
                :icon="Loader"
                size="xl"
                class="animate-spin"
            />
        </template>
        <template #close-icon>
            <SphIcon
                :icon="X"
                size="sm"
            />
        </template>
    </Sonner>
</template>

<style>
@reference '@/assets/styles/index.css';

.toaster{
    [data-sonner-toast]{
        @apply font-sans text-base gap-2;
    }

    [data-sonner-toast][data-styled='true'] {
        @apply whitespace-pre-line;

        [data-icon] {
            @apply size-auto;
        }

        [data-title] {
            @apply text-base font-semibold;
        }

        [data-description] {
            @apply text-sm font-normal;
        }

        [data-close-button] {
            @apply w-auto h-auto bg-gray-100 text-gray-600 border border-gray-300;
            transition: background-color .25s ease-out, border .25s ease-out, color .25s ease-out;

            &:hover{
                @apply bg-gray-200 text-gray-600 border-gray-300;
            }
        }
    }
}
</style>
